// src/pages/Dashboard/index.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  The one on one agreement card for a salesperson.
//   2026-09-27  The owner's weekly check-in, under the mark moment, and the
//               floor this morning in three lines for a manager.
//   2026-09-27  Marks and the meeting reach Today. A mark somebody's manager
//               gave them opens at the top, once. The routine's one rep follows
//               this morning's skill focus, and once the meeting is held a
//               short recap shows the focus and who was called out.
//   2026-08-02  Daily check-in (commitment inspection) + first-run welcome
//               card. Zero placeholder content remains on this page.
//   2026-08-02  Header standardized; Phase 1 checkpoint committed (e765830).
// The daily itinerary. First-run users get the Welcome card explaining the
// J13 loop. Returning users get the check-in on any aged commitment, then
// today's plan, momentum, up-next and the live coach card. Everything derives
// from real progress via buildItinerary.

import { useMemo, useState } from 'react'
import { VStack } from '@chakra-ui/react'
import { useAuth } from '../../hooks/useAuth'
import { atLeast } from '../../lib/nav'
import { firstNameFor } from '../../lib/names.js'
import GreetingHeader from './components/GreetingHeader.jsx'
import FloorToday from './components/FloorToday'
import WelcomeCard from './components/WelcomeCard.jsx'
import DailyCheckIn from './components/DailyCheckIn.jsx'
import MorningRoutine from './components/MorningRoutine.jsx'
import TodaysPlan from './components/TodaysPlan.jsx'
import MomentumRow from './components/MomentumRow.jsx'
import PracticeReps from './components/PracticeReps'
import SecondLevel from './components/SecondLevel'
import UpNext from './components/UpNext.jsx'
import CoachCard from './components/CoachCard.jsx'
import AdminNudge from './components/AdminNudge.jsx'
import AuthorNudge from './components/AuthorNudge.jsx'
import YourWeek from './components/YourWeek.jsx'
import MarkMoment from './components/MarkMoment.jsx'
import MeetingRecap from './components/MeetingRecap.jsx'
import OwnerCheckIn from './components/OwnerCheckIn.jsx'
import ManagerMorningCard from './components/ManagerMorningCard.jsx'
import YourAgreement from './components/YourAgreement.jsx'
import useMeetingToday from './useMeetingToday.js'
import { getStepByNumber } from '../../content/steps'
import { buildItinerary } from './data.js'

function todayKeyUTC() {
  return new Date().toISOString().slice(0, 10)
}

export default function Dashboard() {
  const { user, profile, isAdmin, role } = useAuth()
  // One person. He shares the admin role with Tyler and not the job.
  const author = profile?.is_method_author === true
  const [refresh, setRefresh] = useState(0)

  // Null when unknown. CoachCard and WelcomeCard already guard on this being
  // falsy; baking a greeting word in here defeated those guards and produced
  // "there, you're on Step 2". GreetingHeader applies its own "there" fallback,
  // which is the one place it reads as English.
  const firstName = firstNameFor(profile, user)
  // initials and roleText were read by the header block that the 2026-09-10
  // design pass removed. Nothing renders a person's role at them any more.

  const itinerary = useMemo(() => buildItinerary(user?.id), [user?.id, refresh])

  // This morning's meeting, for the floor only. Managers run it and owners do
  // not sit in it, so neither needs it echoed back on their own Today.
  const onFloor = role === 'employee' && !author
  const meetingToday = useMeetingToday(onFloor ? profile?.dealership_id : null)
  const focusStep = meetingToday.meeting?.step_number ? getStepByNumber(meetingToday.meeting.step_number) : null

  // A commitment is due for inspection once it was made before today.
  const checkInCommitment = itinerary.openCommitments.find(
    (c) => (c.createdAt || '').slice(0, 10) < todayKeyUTC()
  )

  return (
    <VStack align="stretch" spacing={8} maxW="sheet">
      <GreetingHeader
        firstName={firstName}
        planCount={itinerary.plan.length}
        minutes={itinerary.plan.length * 5}
      />

      {/* SOMEBODY NOTICED. Above everything else on the page, because it is
          the one thing here somebody else did for them, and it shows once.
          Anybody can receive one: an owner can recognise a manager. Not the
          author, who has no manager. */}
      {!author && <MarkMoment userId={user?.id} />}

      {/* THE OWNER'S TWO MINUTES A WEEK. The one thing on this page that is
          his to do: look at whether the process ran and recognise the manager
          who ran it. Collapses to a line once done, until Monday. */}
      {role === 'owner' && !author && (
        <OwnerCheckIn userId={user?.id} dealershipId={profile?.dealership_id || null} />
      )}

      {/* A manager lands here, not on /manager/. Three lines of their floor
          this morning and a door to the rest of it. */}
      {role === 'manager' && <ManagerMorningCard dealershipId={profile?.dealership_id || null} />}

      {/* Somebody who has cleared all thirteen. Renders nothing for everyone
          else, which is almost everyone. */}
      <SecondLevel userId={user?.id} />

      {/* A manager's job sits above a salesperson's plan, because it is why
          they opened the app. Silent when nobody is waiting. */}
      {/* He has no floor and no dealership, so this can only ever be an empty
          state at him. */}
      {atLeast(role, 'manager') && !author && <FloorToday />}

      {itinerary.isNewUser && (
        <WelcomeCard firstName={firstName} role={role} />
      )}

      {/* A SALESPERSON'S FRONT DOOR IS THEIR DAY, NOT THE COURSE. Since
          2026-09-27 the first thing they see is a two minute routine:
          yesterday's commitment, how many walked unsold, one rep. The check-in
          is step one of it rather than a card of its own.

          Not for a brand new rep, who has nothing to check, no unsold and no
          step yet, and gets the welcome instead. Not for anybody above the
          floor either: a manager's morning is the manager day, which has its
          own front door, and asking an owner how many walked is asking the
          wrong person. They keep the plain check-in. */}
      {role === 'employee' && !author && !itinerary.isNewUser ? (
        <MorningRoutine
          userId={user?.id}
          commitment={checkInCommitment}
          currentStepInfo={itinerary.currentStep}
          focus={focusStep}
          onCommitmentResolved={() => setRefresh((r) => r + 1)}
        />
      ) : (
        checkInCommitment && (
          <DailyCheckIn
            userId={user?.id}
            commitment={checkInCommitment}
            onResolved={() => setRefresh((r) => r + 1)}
          />
        )
      )}

      {/* What they and their manager agreed in their last one on one, and when
          the manager is coming back to look. Silent when there is nothing to
          carry forward. */}
      {onFloor && <YourAgreement userId={user?.id} />}

      {/* After the meeting is held, never before: a prepared agenda nobody ran
          is a draft, not something that happened. */}
      {onFloor && (
        <MeetingRecap
          meeting={meetingToday.meeting}
          calledOut={meetingToday.calledOut}
          people={meetingToday.people}
          userId={user?.id}
        />
      )}

      <TodaysPlan items={itinerary.plan} />

      <MomentumRow streakDays={itinerary.streakDays} course={itinerary.course} />

      {/* The reps they chose to do. The streak above rewards showing up, which
          is not the same thing and is easier. */}
      {!itinerary.isNewUser && <PracticeReps userId={user?.id} currentStep={itinerary.currentStep} />}

      {itinerary.upNext.length > 0 && <UpNext items={itinerary.upNext} />}

      {!itinerary.isNewUser && <CoachCard firstName={firstName} currentStep={itinerary.currentStep} />}

      {/* Their own week of process work, silent on a zero week and never a
          ranking. Last, because the plan for today is what somebody came for
          and this is a look back at a week they have already had. */}
      {!author && <YourWeek userId={user?.id} />}

      {/* The command centre card is Tyler's. Pointed at the man whose method
          this is it reads as an invitation to inspect his own product. */}
      {isAdmin && !author && <AdminNudge />}
      {author && <AuthorNudge firstName={firstName} />}
    </VStack>
  )
}
